import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bug, CheckCircle, Clock, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BugStatus = () => {
  const { bugId } = useParams();
  const navigate = useNavigate();
  const [bug, setBug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBugStatus();
  }, [bugId]);

  const fetchBugStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bug-reports/status/${bugId}`);
      setBug(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Bug report not found');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="h-8 w-8 text-green-600" />;
      case 'in_progress':
        return <Clock className="h-8 w-8 text-yellow-600" />;
      default:
        return <Bug className="h-8 w-8 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new':
        return 'Submitted';
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#003456]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />
        <div className="max-w-lg mx-auto px-6 py-20 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Report Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/report-bug')}>
            Submit a New Report
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              bug.status === 'resolved' ? 'bg-green-100' : 
              bug.status === 'in_progress' ? 'bg-yellow-100' : 'bg-gray-100'
            }`}>
              {getStatusIcon(bug.status)}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Bug Report Status</h1>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(bug.status)}`}>
              {getStatusLabel(bug.status)}
            </span>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Reference ID</p>
              <p className="font-mono font-medium">{bug.id?.slice(0, 8).toUpperCase()}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Issue Title</p>
              <p className="font-medium">{bug.title}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Category</p>
              <p className="font-medium capitalize">{bug.category?.replace(/_/g, ' ')}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Submitted</p>
              <p className="font-medium">
                {bug.created_at ? new Date(bug.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : '—'}
              </p>
            </div>

            {bug.status === 'resolved' && bug.resolution_message && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 font-medium mb-1">Resolution</p>
                <p className="text-green-800">{bug.resolution_message}</p>
                {bug.resolved_at && (
                  <p className="text-sm text-green-600 mt-2">
                    Resolved on {new Date(bug.resolved_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-8 flex gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Back to Home
            </Button>
            {bug.status === 'resolved' && (
              <Button onClick={() => navigate(`/bug-satisfaction/${bug.id}`)}>
                Rate Our Response
              </Button>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BugStatus;
